(function () {
  const galleryWrapperSelector = '.product__images';
  const teeGallerySelector = '#tee-gallery.tee-gallery-content, #tee-gallery';
  const teeFormSelector = '#tee-artwork-form, .teeinblue-item--product';
  const stickyClass = 'gallery--sticky';
  const hiddenClass = 'gallery--sticky--hide';
  const galleryActiveClass = 'sticky-image-gallery';
  const stickyButtonClass = 'sticky-gallery-button';
  const closeButtonClass = 'gallery--close-button';
  const previewButtonSelector = '.tee-btn--preview';
  const previewModalSelector = '.vm--modal.tee-dialog-gallery, .tee-dialog-gallery .vm--modal';
  const mobilePopupStateEvent = 'caf:mobile-popup-state-change';
  const previewCloseSelector = [
    '.vm--modal.tee-dialog-gallery .tee-dialog-close',
    '.vm--modal.tee-dialog-gallery .vm--close',
    '.vm--modal.tee-dialog-gallery .tee-dcontent-footer .tee-btn',
    '.tee-dialog-gallery .vm--modal .tee-dialog-close',
    '.tee-dialog-gallery .vm--modal .vm--close',
    '.tee-dialog-gallery .vm--modal .tee-dcontent-footer .tee-btn',
    '.vm--container.tee-dialog-gallery .vm--overlay',
    '.tee-dialog-gallery .vm--overlay',
  ].join(', ');

  let stickySuspendedUntil = 0;
  // Keep the PDP form from shifting behind TeeInBlue's preview modal while sticky resumes.
  let preserveStickyPlaceholder = false;
  let resumeTimer = 0;
  let ticking = false;
  let placeholder = null;
  let viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  let viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  let focusSettlingUntil = 0;

  const isEditingText = () => {
    const active = document.activeElement;
    return (
      active instanceof Element &&
      active.matches('textarea, input:not([type="radio"]):not([type="checkbox"]):not([type="range"]):not([type="button"]):not([type="submit"])') &&
      Boolean(active.closest(teeFormSelector))
    );
  };

  // No-op class writes still notify MutationObserver in browsers.
  const setClass = (element, name, enabled) => {
    if (element && element.classList.contains(name) !== enabled) {
      element.classList[enabled ? 'add' : 'remove'](name);
    }
  };

  const isPortraitPhone = () => {
    const width = window.innerWidth || document.documentElement.clientWidth;
    const height = window.innerHeight || document.documentElement.clientHeight;

    // Keep the existing portrait-phone gate, but do not mistake the keyboard for
    // rotation. A real width change always invalidates the pre-keyboard geometry.
    if (
      width !== viewportWidth ||
      (!isEditingText() && window.performance.now() >= focusSettlingUntil)
    ) {
      viewportWidth = width;
      viewportHeight = height;
    }

    return width < 768 && viewportHeight / Math.max(width, 1) > 1.5;
  };

  // v3 (2026-10-04, design-preview/v3/pdp.html "m-typing"): while a TeeInBlue text field is edited on a phone,
  // caf-pdp-personalize-progress.js sets html.caf-pdp-typing and the fixed preview shrinks to a 120px card in
  // the top-right corner (assets/caf-rd-pdp-personalize.css §12), so it yields to the focused input far less.
  const COMPACT_PREVIEW_SIZE = 120;
  const isCompactTyping = () => {
    const root = document.documentElement;
    return Boolean(root && root.classList && root.classList.contains('caf-pdp-typing')) && isEditingText();
  };

  const compactPreviewBottom = () => {
    let header = 0;
    try {
      header = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--caf-sticky-header-h')) || 0;
    } catch (error) {
      header = 0;
    }
    return header + 8 + COMPACT_PREVIEW_SIZE;
  };

  const getStickyPreviewHeight = () => {
    if (isCompactTyping()) {
      return compactPreviewBottom();
    }
    const width = window.innerWidth || document.documentElement.clientWidth;
    return Math.min(Math.max(width * 0.72, 260), 360);
  };

  const getNodes = () => {
    const galleryWrapper = document.querySelector(galleryWrapperSelector);
    const teeGallery = galleryWrapper ? galleryWrapper.querySelector(teeGallerySelector) : null;
    const teeForm = document.querySelector(teeFormSelector);

    return {
      galleryWrapper,
      teeForm,
      teeGallery,
    };
  };

  const isRendered = (element) => {
    if (!(element instanceof Element)) {
      return false;
    }

    const rect = element.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) {
      return false;
    }

    let current = element;

    while (current && current !== document.documentElement) {
      const style = window.getComputedStyle(current);

      if (
        current.hidden ||
        current.getAttribute('aria-hidden') === 'true' ||
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.opacity === '0'
      ) {
        return false;
      }

      current = current.parentElement;
    }

    return true;
  };

  const isPreviewPopupOpen = () =>
    Array.from(document.querySelectorAll(previewModalSelector)).some((modal) => isRendered(modal));

  const isStickySuspended = () => window.performance.now() < stickySuspendedUntil;

  const releasePlaceholder = () => {
    if (!placeholder) {
      return;
    }

    const { wrapper, gallery, originalHeight, originalPriority } = placeholder;
    setClass(gallery, galleryActiveClass, false);
    if (originalHeight) {
      wrapper.style.setProperty('height', originalHeight, originalPriority);
    } else if (wrapper.style.getPropertyValue('height')) {
      wrapper.style.removeProperty('height');
    }
    placeholder = null;
  };

  const setStickyPlaceholderHeight = (nodes) => {
    if (!nodes || !nodes.galleryWrapper) {
      return false;
    }

    const wrapper = nodes.galleryWrapper;
    if (!placeholder) {
      // Reserve the gallery's full in-flow footprint, not the smaller fixed
      // preview. Shrinking it moves short forms across shouldStick's own threshold.
      const height = wrapper.getBoundingClientRect().height;
      if (height <= 0) {
        return false;
      }
      placeholder = {
        wrapper,
        gallery: nodes.teeGallery,
        width: window.innerWidth || document.documentElement.clientWidth,
        height: `${height}px`,
        originalHeight: wrapper.style.getPropertyValue('height'),
        originalPriority: wrapper.style.getPropertyPriority('height'),
      };
    }
    if (wrapper.style.getPropertyValue('height') !== placeholder.height) {
      wrapper.style.setProperty('height', placeholder.height);
      // CSSOM can round fractional pixels; compare its canonical value next time.
      placeholder.height = wrapper.style.getPropertyValue('height');
    }
    return true;
  };

  const scheduleUpdateBurst = () => {
    [0, 180, 420, 900, 1600, 2600, 4200].forEach((delay) => {
      window.setTimeout(scheduleUpdate, delay);
    });
  };

  const scheduleStickyResume = (delay = 320) => {
    if (resumeTimer) {
      window.clearTimeout(resumeTimer);
    }

    stickySuspendedUntil = Math.max(stickySuspendedUntil, window.performance.now() + delay);
    resumeTimer = window.setTimeout(() => {
      resumeTimer = 0;
      stickySuspendedUntil = 0;
      scheduleUpdateBurst();
    }, delay);
  };

  const cancelStickyResume = () => {
    if (!resumeTimer) {
      return;
    }

    window.clearTimeout(resumeTimer);
    resumeTimer = 0;
  };

  const clearStickyState = (nodes, options = {}) => {
    const preserveHeight = Boolean(options.preserveHeight);

    if (!preserveHeight) {
      preserveStickyPlaceholder = false;
    }

    setClass(document.body, stickyClass, false);
    setClass(document.body, hiddenClass, false);

    if (preserveHeight) {
      setStickyPlaceholderHeight(nodes);
    } else {
      releasePlaceholder();
    }

    if (nodes && nodes.teeGallery) {
      setClass(nodes.teeGallery, galleryActiveClass, false);
    }
  };

  const ensureStickyButton = () => {
    let stickyButton = document.querySelector(`.${stickyButtonClass}`);

    if (stickyButton) {
      return stickyButton;
    }

    stickyButton = document.createElement('button');
    stickyButton.type = 'button';
    stickyButton.className = stickyButtonClass;
    stickyButton.setAttribute('aria-label', 'Show product preview');
    stickyButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      setClass(document.body, hiddenClass, false);
      scheduleUpdate();
    });
    document.body.appendChild(stickyButton);

    return stickyButton;
  };

  const ensureCloseButton = (teeGallery) => {
    if (!teeGallery || teeGallery.querySelector(`:scope > .${closeButtonClass}`)) {
      return;
    }

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = closeButtonClass;
    closeButton.setAttribute('aria-label', 'Hide product preview');
    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      setClass(document.body, hiddenClass, true);
      // Hiding the fixed preview must not move the form under a focused input.
      scheduleUpdate();
    });
    teeGallery.appendChild(closeButton);
  };

  const updateStickyPreview = () => {
    const nodes = getNodes();

    if (
      placeholder &&
      (placeholder.wrapper !== nodes.galleryWrapper ||
        placeholder.width !== (window.innerWidth || document.documentElement.clientWidth))
    ) {
      clearStickyState(nodes);
      // Let the vendor gallery return to flow before measuring at the new width.
      scheduleUpdate();
      return;
    }

    if (!isPortraitPhone()) {
      clearStickyState(nodes);
      return;
    }

    if (isStickySuspended() || isPreviewPopupOpen()) {
      clearStickyState(nodes, { preserveHeight: preserveStickyPlaceholder });
      return;
    }

    if (!nodes.galleryWrapper || !nodes.teeGallery || !nodes.teeForm) {
      clearStickyState(nodes);
      return;
    }

    if (placeholder && placeholder.gallery !== nodes.teeGallery) {
      clearStickyState(nodes);
      scheduleUpdate();
      return;
    }

    ensureStickyButton();
    ensureCloseButton(nodes.teeGallery);

    const galleryTop = nodes.galleryWrapper.getBoundingClientRect().top;
    const teeFormBottom = nodes.teeForm.getBoundingClientRect().bottom;
    const stickyPreviewHeight = getStickyPreviewHeight();
    const shouldStick = galleryTop < -10 && teeFormBottom > stickyPreviewHeight + 20;

    if (!shouldStick) {
      clearStickyState(nodes);
      return;
    }

    if (document.body.classList.contains(hiddenClass)) {
      preserveStickyPlaceholder = false;
      return;
    }

    if (isEditingText()) {
      const inputRect = document.activeElement.getBoundingClientRect();
      const visibleTop = window.visualViewport ? window.visualViewport.offsetTop : 0;
      // Keyboard panning can put the input behind the fixed preview. Give the
      // field room without scrolling, refocusing, or changing the page footprint.
      if (inputRect.top < visibleTop + stickyPreviewHeight + 12 && inputRect.bottom > visibleTop) {
        clearStickyState(nodes, { preserveHeight: true });
        return;
      }
    }

    if (!setStickyPlaceholderHeight(nodes)) {
      return;
    }
    setClass(document.body, stickyClass, true);
    setClass(nodes.teeGallery, galleryActiveClass, true);
    preserveStickyPlaceholder = false;
  };

  function scheduleUpdate() {
    if (ticking) {
      return;
    }

    ticking = true;
    window.requestAnimationFrame(() => {
      ticking = false;
      updateStickyPreview();
    });
  }

  const initStickyPreview = () => {
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('orientationchange', scheduleUpdate);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleUpdate);
      window.visualViewport.addEventListener('scroll', scheduleUpdate, { passive: true });
    }
    document.addEventListener('input', scheduleUpdate, true);
    document.addEventListener('change', scheduleUpdate, true);
    document.addEventListener('focusin', scheduleUpdate, true);
    document.addEventListener(
      'focusout',
      () => {
        // Let the keyboard close before refreshing the portrait height baseline.
        focusSettlingUntil = window.performance.now() + 300;
        window.setTimeout(scheduleUpdate, 320);
      },
      true,
    );
    document.addEventListener('teeinblue-event-component-injected', scheduleUpdate);
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;

        if (!(target instanceof Element) || !target.closest(previewButtonSelector)) {
          return;
        }

        const nodes = getNodes();
        preserveStickyPlaceholder = Boolean(placeholder);

        cancelStickyResume();
        clearStickyState(nodes, { preserveHeight: preserveStickyPlaceholder });
        stickySuspendedUntil = Math.max(stickySuspendedUntil, window.performance.now() + 5000);
        window.setTimeout(scheduleUpdate, 400);
        window.setTimeout(scheduleUpdate, 1200);
        window.setTimeout(scheduleUpdate, 5200);
      },
      true,
    );
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;

        if (!(target instanceof Element) || !target.closest(previewCloseSelector)) {
          return;
        }

        scheduleStickyResume(360);
      },
      true,
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || (!isStickySuspended() && !isPreviewPopupOpen())) {
          return;
        }

        scheduleStickyResume(360);
      },
      true,
    );
    document.addEventListener(mobilePopupStateEvent, (event) => {
      if (event.detail && event.detail.open === false) {
        scheduleStickyResume(160);
      }
    });

    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden'],
      childList: true,
      subtree: true,
    });

    window.setTimeout(() => observer.disconnect(), 15000);
    window.setTimeout(scheduleUpdate, 250);
    window.setTimeout(scheduleUpdate, 1000);
    window.setTimeout(scheduleUpdate, 2500);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyPreview);
  } else {
    initStickyPreview();
  }
})();
